import React, { useState, useRef, useCallback } from "react";
import {
  getLeadOwner,
  getLeadSources,
  getLeadStages,
  getEkycStages,
  getSourceMedium,
} from "../Services/leadsService/ManageLeadsService";
import {
  getLeadActivityOwner,
  getLeadActivityTransaction,
  getLeadActivityType,
} from "../Services/leadactivityService/LeadActivityService";

import {
  getUserRole,
  getUserStatus,
  getUserType,
} from "../Services/UserService/ManageUserService";

import ChevronIcon from "./SVGIcons/ChevronIcon";
import ClearIcon from "./SVGIcons/ClearIcon";
import LoadingIcon from "./SVGIcons/LoadingIcon";
import { getDeletedLeadBy } from "../Services/leadsService/DeleteLeadsService";

const DashboardDropdown = ({
  fromType,
  onChange,
  dropdownTitle,
  defaultOption,
}) => {
  const [options, setOptions] = useState([]);
  const [optionsMetadata, setOptionsMetadata] = useState({});
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(defaultOption || "");
  const [searchTerm, setSearchTerm] = useState("");
  const [isHoveredHeader, setIsHoveredHeader] = useState(false);
  const [isHoveredClear, setIsHoveredClear] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const dropdownOptionsRef = useRef(null);

  const fetchDropdownOptions = useCallback(async () => {
    if (options.length > 0) return;

    setLoading(true);
    try {
      let data;
      switch (fromType) {
        case "leadstages":
          data = await getLeadStages();
          setOptions(data.data.LeadStages.map((stage) => stage.prospectstage));
          break;
        case "leadsources":
          data = await getLeadSources();
          setOptions(data.data.LeadSources.map((stage) => stage.source));
          break;
        case "leadowner":
          data = await getLeadOwner();
          setOptions(data.data.LeadOwner.map((stage) => stage.fullname));
          break;
        case "ekycstages":
          data = await getEkycStages();
          setOptions(data.data.LeadEkyc.map((stage) => stage.name));
          break;
        case "sourcemedium":
          data = await getSourceMedium();
          setOptions(
            data.data.sourceMedium.map((stage) =>
              stage.sourcemedium === null
                ? "null"
                : stage.sourcemedium === ""
                ? "empty"
                : stage.sourcemedium
            )
          );
          break;
        case "activitytype":
          data = await getLeadActivityType();
          setOptions(
            data.data.map((activityType) =>
              activityType.name === null
                ? "null"
                : activityType.name === "" || activityType.name === " "
                ? "empty"
                : activityType.name
            )
          );
          break;
        case "activitystatus":
          data = await getLeadActivityTransaction();
          setOptions(
            data.data.map((activityStatus) =>
              activityStatus.status === null
                ? "null"
                : activityStatus.status === "" || activityStatus.status === " "
                ? "empty"
                : activityStatus.status
            )
          );
          break;
        case "activityowner":
          data = await getLeadActivityOwner();
          setOptions(
            data.data.map((activityOwner) =>
              activityOwner.fullname === null
                ? "null"
                : activityOwner.fullname === "" ||
                  activityOwner.fullname === " "
                ? "empty"
                : activityOwner.fullname
            )
          );
          break;
        case "usertype":
          data = await getUserType();
          setOptions(data.data.UserType.map((stage) => stage.usertype));
          break;
        case "userrole":
          data = await getUserRole();
          setOptions(data.data.UserRole.map((stage) => stage.role));
          break;
        case "userstatus":
          data = await getUserStatus();
          setOptions(data.data.UserStatus.map((stage) => stage.status));
          break;
        case "deletedBy":
          data = await getDeletedLeadBy();
          const users = data.data.users;
          const usernames = users.map((user) => user?.username);
          setOptions(usernames);
          // Storing full user objects mapped by username for future use
          const metadata = {};
          users.forEach(user => {
            metadata[user?.username] = user;
          });
          setOptionsMetadata(metadata);
          break;
        default:
          setOptions([]);
      }
    } catch (error) {
      console.error("Failed to fetch options:", error);
    } finally {
      setLoading(false);
    }
  }, [fromType, options.length]);

  const scrollToOption = (index) => {
    const optionsContainer = dropdownOptionsRef.current;
    if (!optionsContainer) return;

    const optionElements = optionsContainer.children;
    if (index < 0 || index >= optionElements.length) return;

    const optionElement = optionElements[index];

    const { offsetTop, clientHeight } = optionElement;
    const { scrollTop, clientHeight: containerHeight } = optionsContainer;

    if (offsetTop < scrollTop) {
      optionsContainer.scrollTop = offsetTop;
    } else if (offsetTop + clientHeight > scrollTop + containerHeight) {
      optionsContainer.scrollTop = offsetTop - containerHeight + clientHeight;
    }
  };

  const handleKeyDown = (event) => {
    if (!isOpen) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setFocusedIndex((prevIndex) => {
          const newIndex =
            prevIndex === filteredOptions.length - 1 ? 0 : prevIndex + 1;
          scrollToOption(newIndex);
          return newIndex;
        });
        break;

      case "ArrowUp":
        event.preventDefault();
        setFocusedIndex((prevIndex) => {
          const newIndex =
            prevIndex === 0 ? filteredOptions.length - 1 : prevIndex - 1;
          scrollToOption(newIndex);
          return newIndex;
        });
        break;

      case "Enter":
        if (focusedIndex >= 0) {
          handleSelect(filteredOptions[focusedIndex]);
        }
        break;

      case "Escape":
        setIsOpen(false);
        setSearchTerm("");
        setFocusedIndex(-1);
        break;

      default:
        break;
    }
  };

  React.useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, focusedIndex]);

  const handleSelect = (value) => {
    setSelectedValue(value);
    // For deletedBy, passing the full user object, This allows flexibility to use any field from the user object
    const valueToPass = optionsMetadata[value] || value;
    onChange(valueToPass);
    setIsOpen(false);
    setSearchTerm("");
    setFocusedIndex(-1);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedValue("");
    onChange(null);
    setSearchTerm("");
    setIsOpen(false);
    setFocusedIndex(-1);
  };

  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  React.useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const styles = {
    dropdownContainer: {
      position: "relative",
      width: "160px",
      border: "1px solid #ddd",
    },
    dropdownHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "8px 12px",
      backgroundColor: "white",
      borderRadius: "6px",
      cursor: "pointer",
      transition: "border-color 0.2s ease",
    },
    dropdownHeaderHover: {
      borderColor: "#999",
    },
    dropdownContent: {
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
      flex: 1,
    },
    dropdownTitle: {
      fontSize: "12px",
      fontWeight: 600,
      color: "#666",
    },
    dropdownValue: {
      fontSize: "14px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    dropdownActions: {
      display: "flex",
      alignItems: "center",
      gap: "4px",
    },
    clearButton: {
      padding: "2px",
      background: "none",
      border: "none",
      borderRadius: "50%",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#666",
      width: "16px",
      height: "16px",
      marginRight: "2px",
    },
    clearButtonHover: {
      backgroundColor: "#f0f0f0",
      color: "#333",
    },
    chevronIcon: {
      transition: "transform 0.2s ease",
      display: "flex",
    },
    chevronIconRotate: {
      transform: "rotate(180deg)",
    },
    dropdownMenu: {
      position: "absolute",
      top: "calc(100% + 4px)",
      left: 0,
      width: "100%",
      backgroundColor: "white",
      border: "1px solid #ddd",
      borderRadius: "6px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      zIndex: 1000,
    },
    searchContainer: {
      display: "flex",
      alignItems: "center",
      padding: "8px",
      borderBottom: "1px solid #eee",
      gap: "8px",
      width: "100%",
      boxSizing: "border-box",
    },
    searchInput: {
      flex: 1,
      border: "none",
      outline: "none",
      fontSize: "14px",
      padding: "4px",
      background: "none",
      width: "100%",
      minWidth: 0,
    },
    dropdownOptions: {
      maxHeight: "240px",
      overflowY: "auto",
    },
    optionItem: {
      padding: "8px 12px",
      fontSize: "14px",
      cursor: "pointer",
      transition: "background-color 0.2s ease",
    },
    optionItemHover: {
      backgroundColor: "#ccc",
    },
    optionItemSelected: {
      backgroundColor: "#f0f0f0",
    },
    loadingContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "16px",
    },
    noOptions: {
      padding: "12px",
      textAlign: "center",
      color: "#666",
      fontSize: "14px",
    },
    searchIconContainer: {
      display: "flex",
      alignItems: "center",
      flexShrink: 0,
    },
  };

  return (
    <div style={styles.dropdownContainer} ref={dropdownRef}>
      <div
        style={{
          ...styles.dropdownHeader,
          ...(isHoveredHeader ? styles.dropdownHeaderHover : {}),
        }}
        onClick={() => {
          setIsOpen(!isOpen);
          fetchDropdownOptions();
        }}
        onMouseEnter={() => setIsHoveredHeader(true)}
        onMouseLeave={() => setIsHoveredHeader(false)}
      >
        <div style={styles.dropdownContent}>
          <span style={styles.dropdownTitle}>{dropdownTitle}</span>
          <span style={styles.dropdownValue}>
            {selectedValue || "-"}
          </span>
        </div>
        <div style={styles.dropdownActions}>
          {selectedValue && (
            <button
              onClick={handleClear}
              style={{
                ...styles.clearButton,
                ...(isHoveredClear ? styles.clearButtonHover : {}),
              }}
              onMouseEnter={() => setIsHoveredClear(true)}
              onMouseLeave={() => setIsHoveredClear(false)}
            >
              <ClearIcon />
            </button>
          )}
          <div
            style={{
              ...styles.chevronIcon,
              ...(isOpen ? styles.chevronIconRotate : {}),
            }}
          >
            <ChevronIcon />
          </div>
        </div>
      </div>

      {isOpen && (
        <div style={styles.dropdownMenu}>
          <div style={styles.searchContainer}>
            <input
              ref={searchInputRef}
              type="text"
              style={styles.searchInput}
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div ref={dropdownOptionsRef} style={styles.dropdownOptions}>
            {loading ? (
              <div style={styles.loadingContainer}>
                <LoadingIcon />
              </div>
            ) : (
              <>
                {filteredOptions.map((option, index) => (
                  <div
                    key={index}
                    style={{
                      ...styles.optionItem,
                      ...(focusedIndex === index ? styles.optionItemHover : {}),
                      ...(selectedValue === option
                        ? styles.optionItemSelected
                        : {}),
                    }}
                    onMouseEnter={() => setFocusedIndex(index)}
                    onClick={() => handleSelect(option)}
                  >
                    {option}
                  </div>
                ))}
                {filteredOptions.length === 0 && (
                  <div style={styles.noOptions}>
                    {searchTerm
                      ? "No matching options"
                      : "No options available"}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardDropdown;
